import { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Mail, Send, CheckCircle, MessageSquare, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('sent');
      setName(''); setEmail(''); setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#030305' }}>
      <Navigation />
      <div className="grid-bg" style={{ paddingTop: '100px', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px' }}>
          <div className="animate-fadeIn">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(0,240,255,0.1)', border: '2px solid rgba(0,240,255,0.3)' }}>
                <MessageSquare size={28} color="#00F0FF" />
              </div>
              <h1 style={{ fontFamily: 'Rajdhani', fontSize: '36px', fontWeight: 700, color: '#fff', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Get In Touch
              </h1>
              <p style={{ fontFamily: 'Outfit', fontSize: '16px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
                Have feedback, questions, or suggestions? We'd love to hear from you.
              </p>
            </div>

            {status === 'sent' ? (
              <div className="text-center py-12 rounded-2xl" style={{ background: 'rgba(0,255,148,0.05)', border: '1px solid rgba(0,255,148,0.2)' }}>
                <CheckCircle size={48} color="#00FF94" className="mx-auto mb-4" />
                <h2 style={{ fontFamily: 'Rajdhani', fontSize: '24px', fontWeight: 700, color: '#00FF94', marginBottom: '8px' }}>Message Sent!</h2>
                <p style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Thank you for your feedback. We'll get back to you soon.</p>
                <button
                  onClick={() => setStatus(null)}
                  data-testid="send-another-btn"
                  className="mt-6 px-6 py-2 rounded-lg"
                  style={{ background: 'rgba(0,255,148,0.1)', border: '1px solid rgba(0,255,148,0.3)', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', color: '#00FF94', textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', display: 'block', marginBottom: '6px' }}>NAME</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <User size={16} color="rgba(255,255,255,0.3)" />
                    <input
                      type="text" value={name} onChange={e => setName(e.target.value)} required
                      placeholder="Your name"
                      data-testid="contact-name"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontFamily: 'Outfit', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', display: 'block', marginBottom: '6px' }}>EMAIL</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Mail size={16} color="rgba(255,255,255,0.3)" />
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      placeholder="your@email.com"
                      data-testid="contact-email"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontFamily: 'Outfit', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', display: 'block', marginBottom: '6px' }}>MESSAGE</label>
                  <textarea
                    value={message} onChange={e => setMessage(e.target.value)} required
                    placeholder="Share your feedback, suggestions, or questions..."
                    rows={5}
                    data-testid="contact-message"
                    style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '12px 16px', color: '#fff', fontFamily: 'Outfit', fontSize: '14px', outline: 'none', resize: 'vertical' }}
                  />
                </div>

                {status === 'error' && (
                  <p data-testid="contact-error" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#FF4444', textAlign: 'center' }}>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit" disabled={status === 'sending'}
                  data-testid="contact-submit-btn"
                  className="w-full py-3.5 rounded-xl flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #00F0FF, #00FF94)', border: 'none', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '15px', color: '#000', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: status === 'sending' ? 0.6 : 1 }}
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
